import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { Form, Field } from 'formik';
import { Row, Col, Form as FormBS } from "react-bootstrap";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import ReactTooltip from "react-tooltip";
import moment from 'moment';

import { chargeCalculation, resetCharges } from "../../../../../../../general_redux/calendar/actions";
import CustomDate from "../../../../../../../components/CustomDate";
import { addDaysToDateObj } from "../../../../../../../common/functions/utils";
import info_icon from '../../../../../../../icons/info_icon_squared.svg';
import styles from '../editBooking.module.css';


const BookingDetails = (props) => {

  const {
    ota,
    bookingId,
    getChannelOptions,
    certainTypesOfReservationsWarning,
    errors,
    values,
    initialValues,
    setFieldValue,
    handleChange,
    handleSubmit,
  } = props;

  const dispatch = useDispatch();

  const rentalList = useSelector(({ rentals }) => rentals && rentals.rentals);
  const allBooking = useSelector(({ calendar }) => calendar && calendar.allBooking);
  const bookingDetails = useSelector(({ calendar }) => calendar && calendar.bookingById);
  const guestList = useSelector(({ guests }) => guests && guests.guestList);
  const relatedReservations = useSelector(({ calendar }) => calendar && calendar.allBooking.filter((booking) => 
    bookingDetails?.channelBookingId && booking.channelBookingId === bookingDetails.channelBookingId
  ));
  const bookingDiscount = useSelector(({ calendar }) => calendar && calendar.bookingDiscount);
  const currentCharges = useSelector(({ calendar }) => calendar && calendar.currentCharges);
  const ratesList = useSelector(({ rates }) => rates && rates.rateSettings);
  const channels = useSelector(({ icalChannels }) => icalChannels && getChannelOptions(icalChannels.channels));
  const generalSetting = useSelector(({ generalSetting }) => generalSetting && generalSetting.setting);
  const { numberOf, allowBookingFor } = useSelector(({ generalSetting }) => generalSetting && generalSetting.setting);

  const colorDictionary = useSelector(({ generalSetting }) => {
    const colorsSetting = generalSetting && generalSetting.setting.colors;
    const map = [];
    for (const key in colorsSetting) {
      if (key.startsWith('color')) {
        map.push(colorsSetting[key]);
      };
    };
    return map;
  });

  const [bookingData, setBookingData] = useState(bookingDetails);
  const [guestDetails, setGuestDetails] = useState(null);
  const [bookerDetails, setBookerDetails] = useState(null);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(0);

  const findRentalDetail = (rentalId) => {
    const currency = generalSetting.currency.value;
    const rentalDetail = rentalList.find((rental) => Number(rental.id) === Number(rentalId));
    return rentalDetail || {
      id: '',
      arrive: '',
      maxOccup: 1,
      maxChild: 0,
      checkInTimeAt: 'from',
      checkOutTimeAt: 'until',
      checkinTime: '15:00',
      checkoutTime: '10:00',
      currency: currency,
    };
  };

  const findRateDetail = (rentalId) => {
    const findRate = ratesList.find((rate) => Number(rate.rentalId) === Number(rentalId));
    return findRate || { minimumStayRequirement: 1, maximumStayRequirement: 10 };
  };

  const guestName = guestDetails ? guestDetails?.name : bookingData?.guestName;
  const bookerName = bookerDetails ? bookerDetails?.name : bookingData?.guestName;
  const paymentOpt = ['Payment Pending', 'Partially Paid', 'Paid'];

  const findContactDetail = (contactId) => {
    const findContact = guestList.find(guest => guest.id === contactId);
    return findContact || "";
  };

  useEffect(() => {

    const rateDetail = findRateDetail(values.rentalId);
    setFieldValue('minStay', rateDetail.minimumStayRequirement);
    setFieldValue('maxStay', rateDetail.maximumStayRequirement);

  }, [])
  
  useEffect(() => {

    const updatedBookingData = allBooking.find((booking) => booking.id === bookingData?.id);
    setBookingData(updatedBookingData);
    setGuestDetails(findContactDetail(updatedBookingData?.guestId));
    setBookerDetails(findContactDetail(updatedBookingData?.bookerId));
    setFieldValue('price', updatedBookingData?.price);
    setFieldValue('guestId', updatedBookingData?.guestId);
    setFieldValue('bookerId', updatedBookingData?.bookerId);

    dispatch(resetCharges());

  }, [dispatch])

  useEffect(() => {

    if (values.arrive && values.depart && values.rentalId && values.noOfGuests && (values.noOfAdults > 0) && bookingDiscount.chargeAmount !== undefined && bookingDiscount.discountAmount !== undefined) {

      if (shouldFetch > 0) {

        dispatch(chargeCalculation({
          arrive: moment(values.arrive).format().slice(0, 10),
          depart: moment(values.depart).format().slice(0, 10),
          noOfAdults: Number(values.noOfAdults),
          noOfChildren: Number(values.noOfChildren),
          rentalId: Number(values.rentalId),
          extraCharge: Number(bookingDiscount.chargeAmount),
          discount: Number(bookingDiscount.discountAmount),
          bookingType: bookingData?.bookingType,
        }))

        setFieldValue('nights', moment(values.depart).diff(moment(values.arrive), 'days'));
        setDisableSubmit(true);

      };

      setShouldFetch(value => value + 1);

    };

  }, [values.arrive, values.depart, values.rentalId, values.noOfGuests])

  useEffect(() => {

    if (currentCharges && shouldFetch > 0) {

      if (currentCharges && !currentCharges.maxStay) {
        setFieldValue('maxStay', currentCharges.maxStayConstrain);
      };

      if (currentCharges && !currentCharges.minStay) {
        setFieldValue('minStay', currentCharges.minStayConstrain);
      };

      setFieldValue('price', currentCharges.total);
      setDisableSubmit(false);
    };

  }, [currentCharges])

  const payloads = () => ({
    bookingPayload: {
      id: values?.id,
      bookingType: values?.bookingType,
      rentalId: Number(values.rentalId),
      guestId: Number(values?.guestId),
      bookerId: Number(values?.bookerId),
      noOfGuests: Number(values?.noOfGuests),
      noOfAdults: Number(values?.noOfAdults),
      noOfChildren: Number(values?.noOfChildren),
      noOfBabies: Number(values?.noOfBabies),
      noOfPets: Number(values?.noOfPets),
      netAmount: Number(values?.netAmount),
      additionalGuestNames: values?.additionalGuestNames,
      arrive: moment(values?.arrive).utc(true).format("YYYY-MM-DD"),
      depart: moment(values?.depart).utc(true).format("YYYY-MM-DD"),
      arriveUTC: moment(values?.arrive).utc(true).format("YYYY-MM-DD HH:mm:ss"),
      departUTC: moment(values?.depart).utc(true).format("YYYY-MM-DD HH:mm:ss"),
      checkInTimeAt: values?.checkInTimeAt,
      checkOutTimeAt: values?.checkOutTimeAt,
      checkInTime: values?.checkInTime,
      checkOutTime: values?.checkOutTime,
      nights: values?.nights,
      status: values?.status,
      source: values?.source,
      tax: Number(Number(currentCharges && currentCharges?.taxes || bookingDiscount.tax).toFixed(2)),
      fees: Number(Number(currentCharges && currentCharges?.fees || bookingDiscount.fee).toFixed(2)),
      extraGuestFee: Number(Number(currentCharges && currentCharges?.extra_guest_fees || bookingDiscount.extraGuestFee).toFixed(2)),
      baseRate: Number(Number(currentCharges && currentCharges?.daily_rate || bookingDiscount.baseRate).toFixed(2)),
      discountName: bookingDiscount.discountName,
      discountAmount: Number(Number(bookingDiscount.discountAmount).toFixed(2)),
      chargeName: bookingDiscount.chargeName,
      chargeAmount: Number(Number(bookingDiscount.chargeAmount).toFixed(2)),
      price: Number(Number(values?.price).toFixed(2)),
      paymentStatus: values?.paymentStatus,
      miniNotes: values?.miniNotes,
      notes: values?.notes,
      color: values?.color,
    },
    discountPayload: {
      bookingId: values?.id,
      rentalId: Number(values.rentalId),
      bookingType: values?.bookingType,
      discountName: bookingDiscount.discountName,
      discountAmount: Number(Number(bookingDiscount.discountAmount).toFixed(2)),
      chargeName: bookingDiscount.chargeName,
      chargeAmount: Number(Number(bookingDiscount.chargeAmount).toFixed(2)),
      tax: Number(Number(currentCharges && currentCharges?.taxes || bookingDiscount.tax).toFixed(2)),
      fees: Number(Number(currentCharges && currentCharges?.fees || bookingDiscount.fee).toFixed(2)),
      extraGuestFee: Number(Number(currentCharges && currentCharges?.extra_guest_fees || bookingDiscount.extraGuestFee).toFixed(2)),
      baseRate: Number(Number(currentCharges && currentCharges?.daily_rate || bookingDiscount.baseRate).toFixed(2)),
      totalPrice: Number(currentCharges && currentCharges?.total || bookingDiscount?.totalPrice),
      bookingPrice: Number(currentCharges && currentCharges?.total || values?.price),
      nights: Number(values?.nights),
      noOfAdults: Number(values?.noOfAdults),
      noOfChildren: Number(values?.noOfChildren),
    },
    guestPayload: values.guest,
    bookerPayload: values.booker,
  });

  const Payment = (props) => (
    <div key={props.key} className={props.onHide === "first-payment-component" ? "d-none d-lg-block" : "d-block d-lg-none"}>
      <hr />
      <h3 className={styles.header}>Payment</h3>
      <div className={styles.paymentDetailsContainer}>
        <div className={`d-flex`} key="price">
          <div className={`d-flex ${styles.finalPriceLabel} ${styles.form_title}`}>
            Total:
            <InfoOutlinedIcon
              fontSize="small"
              color="inherit"
              data-tip
              data-for="finalPriceIcon"
              className={styles.toolTipIcon}
              style={{ marginBottom: "8px" }}
            />
            <ReactTooltip place="bottom" type="dark" id="finalPriceIcon" effect="solid" className={styles.toolTip}>
              <span>
                Considers rates, extra guest fees, fees, and taxes.
                These can be managed by clicking on the Rates icon.
                Any discount or charge you have set above are also considered here.
                You can manually overwrite the total for this booking by editing the booking later.
              </span>
            </ReactTooltip>
          </div>
          <div className="m-0 p-0 col-lg-7">
            <Field
              type="text"
              name="price"
              value={Number(values.price).toFixed(2)}
              className={(errors.price) ? `is-invalid ${styles.form_entry_err}` : `${styles.finalPriceInput} ${styles.form_entry}`}
              disabled={true}
            />
            <div className={errors.price ? styles.form_error : styles.form_error_hidden}>
              {errors.price}
            </div>
          </div>
        </div>
        <div className={`d-flex`} key="paymentStatus">
          <div className={`d-flex ${styles.paymentStatusLabel} ${styles.form_title}`}>
            Status:
          </div>
          <div className="m-0 p-0 col-lg-7">
            <Field
              as={"select"}
              name="paymentStatus"
              className={(errors.paymentStatus) ? `is-invalid ${styles.form_entry_err}` : `${styles.paymentStatusInput} ${styles.form_entry}`}
            >
              <option key={""} value={""} disabled={true} label={"--Select Status--"} />
              {paymentOpt.map((opt) => (
                <option value={opt} key={opt}>{opt}</option>
              ))}
            </Field>
          </div>
        </div>
      </div>
    </div>
  )

  const SelectColor = (props) => (
    <div key="select-color" className={props.onHide === "first-color-component" ? "d-none d-lg-block" : "d-block d-lg-none"}>
      <div className={`d-flex ${styles.form_title} ${styles.labelColor}`}>
        Choose colour:
      </div>
      <div className={styles.containerColor}>
        {colorDictionary.map((item, index) => (
          <div
            className={`${styles.containerBox} ${(values.color === item) ? ` active1` : ``}`}
            style={{ background: `${item}` }}
            onClick={() => setFieldValue("color", item)}
            key={index}
          />
        ))}
        <Field
          type="color"
          name="color"
          value={values.color}
          onChange={handleChange}
          className={styles.form_entry_color}
        />
      </div>
    </div>
  )


  return (
    <Form
        onSubmit={(e) => {
            setFieldValue('payloads', payloads());
            handleSubmit(e);
        }}
        autoComplete="off"
    >
      <div className="col-12">
        {['weekly', 'monthly'].includes(bookingData?.bookingType) && (
          <div className="mb-3 d-flex align-content-center">
            <div className={styles.warningIconContainer}>
              <img src={info_icon} alt="Info Icon Squared" className={styles.warningIconOne} />
            </div>
            <div>
              {`This is a fixed-term reservation. ${bookingData?.bookingType === 'weekly' ? 'Weekly' : 'Monthly'} pricing is being used.`}
              <InfoOutlinedIcon
                fontSize="small"
                color="inherit"
                data-tip
                data-for="fixedTerm"
                className={styles.toolTipIcon}
                style={{marginTop: "-12px"}}
              />
              <ReactTooltip place="bottom" type="dark" id="fixedTerm" effect="solid" className={styles.toolTip}>
                <span>
                  <p>
                    Fixed-term reservations use special rate settings, which you might have set up differently than your nightly rates. If you make any changes to this reservation, the fixed nightly rate will be used to calculate the extra nights.
                  </p>
                  <p>
                    If you plan on changing the length of stay substantially in this fixed-term booking, consider the effect on your pricing. You can correct unintended effects by overwriting the price under the payment tab or making a standard nightly reservation instead.
                  </p>
                </span>
              </ReactTooltip>
            </div>
          </div>
        )}
      </div>
      <div className="col-12">
        {bookingData?.bookingType === "channelBooking" && (
          <div className="mb-3 d-flex align-content-center">
            <div className={styles.warningIconContainer}>
              <img src={info_icon} alt="Info Icon Squared" className={styles.warningIconOne} />
            </div>
            <div>
              This is a channel reservation. Changes made here may not update the original reservation on the OTA.
              <InfoOutlinedIcon
                fontSize="small"
                color="inherit"
                data-tip
                data-for="fixedTerm"
                className={styles.toolTipIcon}
                style={{marginTop: "-12px"}}
              />
              <ReactTooltip place="bottom" type="dark" id="fixedTerm" effect="solid" className={styles.toolTip}>
                <p>
                  This reservation was imported from an OTA. Changes to key details such as dates, guest info, or pricing <span className={styles.subHeader}>will not be reflected on the OTA.</span> To update these details, we recommend using the appropriate tools or interfaces provided by the OTA.
                </p>
                <p>
                  <span className={styles.subHeader}>Important:</span> If you modify the arrival or departure dates, or cancel this reservation within Beehaz, you risk creating a double booking.
                </p>
                <p>
                  Although the original reservation on the OTA remains unchanged, Beehaz will update availability across your connected channels, potentially opening up dates that are still booked on the OTA.
                </p>
              </ReactTooltip>
            </div>
          </div>
        )}
      </div>
      <div className={`col ${styles.contactSection}`}>
        Guest
      </div>
      <div className={styles.mainContainer}>
        <div className={`col-lg-6 col-md-12 px-xs-1`}>
          <div className="pb-2">
            {bookingData?.bookingType === 'channelBooking' ?
              <>
                <div className={styles.subHeader}>{`${guestName || "Guest"}`}</div>
                {/* TODO: create mapper according to the OTA dictionary */}
                {/* <div className={styles.subHeader}>{`${(ota && guestName) ? `${ota} ${guestName}` : `${ota} Guest`}`}</div> */}
                <div className={styles.defaultFont}>{guestDetails?.emailId}</div>
                <div className={styles.defaultFont}>{bookingData?.reservationLink && `Reservation link: ${bookingData?.reservationLink}`}</div>
              </>
              :
              <>
                <div className={styles.subHeader}>{guestDetails?.name}</div>
                <div className={styles.defaultFont}>{guestDetails?.emailId}</div>
                <div className={styles.defaultFont}>{guestDetails?.phoneNo}</div>
              </>
            }
          </div>
        </div>
        <div className={`col-lg-6 col-md-12`}>
          <div className="mb-3">
            <div className={`${styles.smallHeader} font-weight-normal`}>
              More info:
              <InfoOutlinedIcon
                fontSize="small"
                color="inherit"
                data-tip
                data-for="guest.notes"
                className={styles.toolTipIcon}
                style={{ marginBottom: "8px" }}
              />
              <ReactTooltip place="bottom" type="dark" id="guest.notes" effect="solid" className={styles.toolTip}>
                <span>
                  Write more information or notes specific to your guest here. These can be retrieved later if the guest makes another reservation.
                </span>
              </ReactTooltip>
            </div>
            <Field
              type={'text'}
              name={'guest.notes'}
              onChange={handleChange}
              value={values.guest.notes}
              className={`form-control ${styles.form_entry} w-100`}
            />
          </div>
        </div>
      </div>
      <div className={`${(values.guestId === values.bookerId) && values.checkboxAdditionalGuests ? "mb-lg-4" : ""} ${styles.checkboxContainer} ${styles.mainContainer}`}>
        <div className={`col-lg-6 col-md-12 d-flex`} key="checkboxAdditionalGuests">
          <FormBS.Check
            type="checkbox"
            name="checkboxAdditionalGuests"
            checked={values.checkboxAdditionalGuests}
            onChange={() => {
              if (values.checkboxAdditionalGuests) {
                setFieldValue("additionalGuestNames", initialValues.additionalGuestNames);
              };
              setFieldValue("checkboxAdditionalGuests", !values.checkboxAdditionalGuests);
            }}
            className={styles.checkboxAdditionalGuests}
          />
          <div className={`w-100 d-flex ${styles.form_title}`}>
            Additional guests
          </div>
        </div>
        {values.checkboxAdditionalGuests && (
          <div className={`col-lg-6 col-md-12`} key="additionalGuestNames">
            <div>
              Additional guest names:
            </div>
            <div>
              <Field
                type="text"
                name="additionalGuestNames"
                value={values.additionalGuestNames}
                onChange={handleChange}
                className={`form-control ${styles.form_entry} w-100`}
              />
              <div className={errors.additionalGuestNames ? styles.form_error : styles.form_error_hidden}>
                {errors.additionalGuestNames}
              </div>
            </div>
          </div>
        )}
      </div>
      {!(values.guestId === values.bookerId) && (
        <>
          <div className={`col ${styles.contactSection}`}>
            Booker
            <InfoOutlinedIcon
              fontSize="small"
              color="inherit"
              data-tip
              data-for="bookerSection"
              className={styles.toolTipIcon}
              style={{ marginBottom: "8px" }}
            />
            <ReactTooltip place="bottom" type="dark" id="bookerSection" effect="solid" className={styles.toolTip}>
              <span>
                The booker is the person who made the reservation. You can edit their details under the "Guest Details" tab.
              </span>
            </ReactTooltip>
          </div>
          <div className={styles.mainContainer}>
            <div className={`col-lg-6 col-md-12 px-xs-1`}>
              <div className="pb-2">
                {bookingData?.bookingType === 'channelBooking' ?
                  <>
                    <div className={styles.subHeader}>{`${bookerName || "Booker"}`}</div>
                    {/* TODO: create mapper according to the OTA dictionary */}
                    {/* <div className={styles.subHeader}>{`${(ota && bookerName) ? `${ota} ${bookerName}` : `${ota} Guest`}`}</div> */}
                    <div className={styles.defaultFont}>{bookerDetails?.emailId}</div>
                    <div className={styles.defaultFont}>{bookingData?.reservationLink && `Reservation link: ${bookingData?.reservationLink}`}</div>
                  </>
                  :
                  <>
                    <div className={styles.subHeader}>{bookerDetails?.name}</div>
                    <div className={styles.defaultFont}>{bookerDetails?.emailId}</div>
                    <div className={styles.defaultFont}>{bookerDetails?.phoneNo}</div>
                  </>
                }
              </div>
            </div>
            <div className={`col-lg-6 col-md-12`}>
              <div className="mb-3">
                <div className={`${styles.smallHeader} font-weight-normal`}>
                  More info:
                  <InfoOutlinedIcon
                    fontSize="small"
                    color="inherit"
                    data-tip
                    data-for="booker.notes"
                    className={styles.toolTipIcon}
                    style={{ marginBottom: "8px" }}
                  />
                  <ReactTooltip place="bottom" type="dark" id="booker.notes" effect="solid" className={styles.toolTip}>
                    <span>
                      Write more information or notes specific to your contact here. These can be retrieved later if the contact makes another reservation.
                    </span>
                  </ReactTooltip>
                </div>
                <Field
                  type={'text'}
                  name={'booker.notes'}
                  onChange={handleChange}
                  value={values.booker.notes}
                  className={`form-control ${styles.form_entry} w-100`}
                />
              </div>
            </div>
          </div>
        </>
      )}
      <hr className="my-0 mb-3" />
      <Col className="pb-3">
        <div className={styles.header}>
          Booking
        </div>
        <div className={styles.defaultFont}>{`Booking Number: ${bookingData?.bookingNumber}`}</div>
        <div className={styles.defaultFont}>{`OTA Reservation Number: ${values.channelBookingId}`}</div>
      </Col>
      <div className={styles.mainContainer}>
        <Col lg={6}>
          <div className={`d-flex ${styles.form_wrapper}`} key="selectRental">
            <div className={`m-0 p-0 col-lg-5 d-flex ${styles.form_title}`}>
              Select rental*:
            </div>
            <div className="m-0 p-0 col-lg-7">
              <Field
                as="select"
                type="number"
                name="rentalId"
                value={values.rentalId}
                className={errors.rentalId ? `is-invalid ${styles.form_entry_err}` : styles.form_entry}
                onChange={(e) => {
                  const currentRental = findRentalDetail(e.target.value);
                  const rateDetail = findRateDetail(e.target.value);
                  setFieldValue('rentalId', currentRental.id);
                  setFieldValue('checkInTimeAt', currentRental.checkInTimeAt);
                  setFieldValue('checkOutTimeAt', currentRental.checkOutTimeAt);
                  setFieldValue('checkInTime', currentRental.checkinTime);
                  setFieldValue('checkOutTime', currentRental.checkoutTime);
                  setFieldValue('maxOccup', currentRental.maxOccup);
                  setFieldValue('maxChild', currentRental.maxChild);
                  setFieldValue('minStay', rateDetail.minimumStayRequirement);
                  setFieldValue('maxStay', rateDetail.maximumStayRequirement);
                  certainTypesOfReservationsWarning();
                }}
              >
                <option value={0} label={"---Select Rental---"} />
                {rentalList.map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.name}</option>
                ))}
              </Field>
              <div className={errors.rentalId ? styles.form_error : styles.form_error_hidden}>
                {errors.rentalId}
              </div>
            </div>
          </div>
          <div className={`d-flex ${styles.form_wrapper}`} key="arrive">
            <div className={`m-0 p-0 col-lg-5 d-flex ${styles.form_title}`}>
              Arrival date*:
            </div>
            <div className="m-0 p-0 col-lg-7">
              <CustomDate
                name="arrive"
                value={values.arrive}
                handleChange={(date) => setFieldValue("arrive", date)}
                onBlur={certainTypesOfReservationsWarning}
                hasError={errors.arrive}
                customClass={errors.arrive ? styles.form_entry_err : styles.form_entry}
                max={values.depart ?
                  new Date(`${moment(values.depart).format("YYYY-MM-DD")}`)
                  :
                  new Date(`${moment().add(numberOf, (allowBookingFor === "months" ? 'M' : 'y')).format('YYYY-MM-DD')}`)
                }
              />
              <div className={errors.arrive ? styles.form_error : styles.form_error_hidden}>
                {errors.arrive}
              </div>
            </div>
          </div>
          <div className={`d-flex ${styles.form_wrapper}`} key="depart">
            <div className={`m-0 p-0 col-lg-5 d-flex ${styles.form_title}`}>
              Departure date*:
            </div>
            <div className="m-0 p-0 col-lg-7">
              <CustomDate
                name="depart"
                value={values.depart}
                handleChange={(date) => setFieldValue("depart", date)}
                onBlur={certainTypesOfReservationsWarning}
                hasError={errors.depart}
                customClass={errors.depart ? styles.form_entry_err : styles.form_entry}
                min={values?.arrive ? addDaysToDateObj(values.arrive, 1) : null}
                max={new Date(`${moment().add(numberOf, allowBookingFor === "months" ? "M" : "y").format("YYYY-MM-DD")}`)}
              />
              <div className={errors.depart ? styles.form_error : styles.form_error_hidden}>
                {errors.depart}
              </div>
            </div>
          </div>
          <div className={`d-flex ${styles.form_wrapper}`} key="nights">
            <div className={`m-0 p-0 col-lg-5 d-flex ${styles.form_title}`}>
              Total nights:
            </div>
            <div className="m-0 p-0 col-lg-7">
              <Field
                type="text"
                name="nights"
                value={values.nights}
                disabled={true}
                className={errors.nights ? `is-invalid ${styles.form_entry_err}` : styles.form_entry}
              />
              <div className={errors.nights ? styles.form_error : styles.form_error_hidden}>
                {errors.nights}
              </div>
            </div>
          </div>
          <SelectColor onHide="first-color-component" />
          <div className={`d-flex ${styles.form_wrapper}`} key="checkintime">
            <div className={`m-0 p-0 col-lg-5 d-flex ${styles.form_title}`}>
              Check-in time*:
            </div>
            <div className="m-0 p-0 col-lg-7">
              <div className="d-flex">
                <FormBS.Control
                  as="select"
                  name="checkInTimeAt"
                  onChange={handleChange}
                  isInvalid={!!errors.checkInTimeAt}
                  value={values.checkInTimeAt}
                  style={{fontSize:'inherit'}}
                  className={`${styles.form_entry_half}`}
                >
                  <option value="at" key="at">at &gt;&gt;</option>
                  <option value="from" key="from">from &gt;&gt;</option>
                </FormBS.Control>
                <div className="col-7 m-0 p-0">
                  <Field
                    type="time"
                    name="checkInTime"
                    value={values.checkInTime}
                    onChange={handleChange}
                    onBlur={certainTypesOfReservationsWarning}
                    className={errors.checkInTime ? `w-100 is-invalid ${styles.form_entry_half_err}` : `w-100 ${styles.form_entry_half}`}
                  />
                  <div className={errors.checkInTime ? styles.form_error : styles.form_error_hidden}>
                    {errors.checkInTime}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={`d-flex ${styles.form_wrapper}`} key="checkouttime">
            <div className={`m-0 p-0 col-lg-5 d-flex ${styles.form_title}`}>
              Check-out time*:
            </div>
            <div className="m-0 p-0 col-lg-7">
              <div className="d-flex">
                <FormBS.Control
                  as="select"
                  name="checkOutTimeAt"
                  onChange={handleChange}
                  isInvalid={!!errors.checkOutTimeAt}
                  value={values.checkOutTimeAt}
                  style={{fontSize:'inherit'}}
                  className={`${styles.form_entry_half}`}
                >
                  <option value="at" key="at">at &gt;&gt;</option>
                  <option value="until" key="until">until &gt;&gt;</option>
                </FormBS.Control>
                <div className="col-7 m-0 p-0">
                  <Field
                    type="time"
                    name="checkOutTime"
                    value={values.checkOutTime}
                    onChange={handleChange}
                    onBlur={certainTypesOfReservationsWarning}
                    className={errors.checkOutTime ? `w-100 is-invalid ${styles.form_entry_half_err}` : `w-100 ${styles.form_entry_half}`}
                  />
                  <div className={errors.checkOutTime ? styles.form_error : styles.form_error_hidden}>
                    {errors.checkOutTime}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={`d-flex ${styles.form_wrapper}`} key="source">
            <div className={`m-0 p-0 col-lg-5 d-flex ${styles.form_title}`}>
              Source:
            </div>
            <div className="m-0 p-0 col-lg-7">
              <Field
                as="select"
                name="source"
                value={values.source}
                onChange={handleChange}
                className={errors.source ? `is-invalid ${styles.form_entry_err}` : styles.form_entry}
              >
                {channels.map((opt, index) => (
                  <option key={index} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Field>
            </div>
          </div>
          <Payment onHide="first-payment-component" />
        </Col>
        <Col>
          <div className={`d-flex ${styles.form_wrapper}`} key="noOfAdults">
            <div className={`m-0 p-0 col-lg-5 d-flex ${styles.form_title}`}>
              Adults:
            </div>
            <div className="m-0 p-0 col-lg-7">
              <Field
                type="text"
                name="noOfAdults"
                value={values.noOfAdults}
                onChange={(e) => {
                  const adultValue = e.target.value;
                  setFieldValue("noOfAdults", adultValue);
                  setFieldValue("noOfGuests", Number(adultValue) + Number(values.noOfChildren));
                }}
                placeholder={0}
                className={`w-100 ${errors.noOfAdults ? `is-invalid ${styles.form_entry_err}` : styles.form_entry}`}
              />
              <div className={errors.noOfAdults ? styles.form_error : styles.form_error_hidden}>
                {errors.noOfAdults}
              </div>
            </div>
          </div>
          <div className={`d-flex ${styles.form_wrapper}`} key="noOfChildren">
            <div className={`m-0 p-0 col-lg-5 d-flex ${styles.form_title}`}>
              Children:
            </div>
            <div className="m-0 p-0 col-lg-7">
              <Field
                type="text"
                name="noOfChildren"
                value={values.noOfChildren || ''}
                onChange={(e) => {
                  const childrenValue = e.target.value;
                  setFieldValue("noOfChildren", childrenValue);
                  setFieldValue("noOfGuests", Number(childrenValue) + Number(values.noOfAdults));
                }}
                placeholder={0}
                className={`w-100 ${errors.noOfChildren ? `is-invalid ${styles.form_entry_err}` : styles.form_entry}`}
              />
              <div className={errors.noOfChildren ? styles.form_error : styles.form_error_hidden}>
                {errors.noOfChildren}
              </div>
            </div>
          </div>
          <div className={`d-flex ${styles.form_wrapper}`} key="noOfBabies">
            <div className={`m-0 p-0 col-lg-5 d-flex ${styles.form_title}`}>
              Babies:
              <InfoOutlinedIcon
                fontSize="small"
                color="inherit"
                data-tip
                data-for="noOfBabies"
                className={styles.toolTipIcon}
              />
              <ReactTooltip place="bottom" type="dark" id="noOfBabies" effect="solid" className={styles.toolTip}>
                <span>
                  Babies are not counted in the number of total guests (which consists of the total number of adults + the total number of children).
                  This means they do not have any effect on the price calculation (while the number of adults and children influence the price when you have per person fees in your pricing).
                </span>
              </ReactTooltip>
            </div>
            <div className="m-0 p-0 col-lg-7">
              <Field
                type="text"
                name="noOfBabies"
                value={values.noOfBabies || ''}
                onChange={handleChange}
                placeholder={0}
                className={`w-100 ${errors.noOfBabies ? `is-invalid ${styles.form_entry_err}` : styles.form_entry}`}
              />
              <div className={errors.noOfBabies ? styles.form_error : styles.form_error_hidden}>
                {errors.noOfBabies}
              </div>
            </div>
          </div>
          <div className={`d-flex ${styles.form_wrapper}`} key="totalGuest">
            <div className={`m-0 p-0 col-lg-5 d-flex ${styles.form_title}`}>
              Number of guests:
            </div>
            <div className="m-0 p-0 col-lg-7">
              <Field
                type="text"
                name="noOfGuests"
                value={values.noOfGuests}
                disabled={true}
                className={`w-100 ${errors.noOfGuests ? `is-invalid ${styles.form_entry_err}` : styles.form_entry}`}
              />
              <div className={errors.noOfGuests ? styles.form_error : styles.form_error_hidden}>
                {errors.noOfGuests}
              </div>
            </div>
          </div>
          <div className={`d-flex ${styles.form_wrapper}`} key="noOfPets">
            <div className={`m-0 p-0 col-lg-5 d-flex ${styles.form_title}`}>
              Pets:
            </div>
            <div className="m-0 p-0 col-lg-7">
              <Field
                type="text"
                name="noOfPets"
                value={values.noOfPets || ''}
                onChange={handleChange}
                placeholder={0}
                className={`w-100 ${errors.noOfPets ? `is-invalid ${styles.form_entry_err}` : styles.form_entry}`}
              />
              <div className={errors.noOfPets ? styles.form_error : styles.form_error_hidden}>
                {errors.noOfPets}
              </div>
            </div>
          </div>
          <div className={`d-flex ${styles.form_wrapper}`} key="miniNotes">
            <div className={`m-0 p-0 col-lg-5 d-flex ${styles.form_title}`}>
              Mini notes:
              <InfoOutlinedIcon
                fontSize="small"
                color="inherit"
                data-tip
                data-for="miniNotes"
                className={styles.toolTipIcon}
              />
              <ReactTooltip place="bottom" type="dark" id="miniNotes" effect="solid" className={styles.toolTip}>
                <span>
                  Write a short note to be displayed in the booking information summary box that appears when you click on a reservation in the calendar.
                </span>
              </ReactTooltip>
            </div>
            <div className="m-0 p-0 col-lg-7">
              <Field
                type="text"
                name="miniNotes"
                value={values.miniNotes}
                onChange={handleChange}
                className={`w-100 ${errors.miniNotes ? `is-invalid ${styles.form_entry_err}` : styles.form_entry}`}
              />
              <div className={errors.miniNotes ? styles.form_error : styles.form_error_hidden}>
                {errors.miniNotes}
              </div>
            </div>
          </div>
          <div className={`${styles.form_wrapper}`} key="notes">
            <div className={`m-0 p-0 d-flex ${styles.form_title}`}>
              <p style={{ fontWeight: "bold", marginBlock: "4px" }}>
                Notes:
              </p>
              <InfoOutlinedIcon
                fontSize="small"
                color="inherit"
                data-tip
                data-for="notes"
                className={styles.toolTipIcon}
              />
              <ReactTooltip place="bottom" type="dark" id="notes" effect="solid" className={styles.toolTip}>
                <span>
                  Write longer notes specific to this booking here.
                </span>
              </ReactTooltip>
            </div>
            <div className="m-0 p-0 col-lg-12">
              <Field
                as="textarea"
                name="notes"
                value={values.notes}
                onChange={handleChange}
                rows={9}
                autoComplete="off"
                className={`w-100 h-100 ${errors.notes ? `is-invalid ${styles.form_entry_err}` : styles.form_entry}`}
              />
              <div className={errors.notes ? styles.form_error : styles.form_error_hidden}>
                {errors.notes}
              </div>
            </div>
          </div>
          <SelectColor onHide="second-color-component" />
          <Payment onHide="second-payment-component" />
        </Col>
      </div>
      {relatedReservations.length > 0 && (
        <>
          <hr className="mt-4 my-lg-3" />
          <div className={styles.mainContainer}>
            <div className={`col-lg-6 col-md-12 px-xs-1`}>
              <div className="pb-3">
                <span className={styles.subHeader}>
                  Related reservations
                </span>
                <br />
                <span>
                  This booking is part of a multi-rental reservation.
                </span>
                <br />
                <span>
                  See the linked reservations below:
                </span>
              </div>
              {relatedReservations.map((booking, index) => (
                <div key={index} className="pb-3">
                  <div className={styles.defaultFont}>{`Booking Number: ${booking?.suBookingId}`}</div>
                  <div className={styles.defaultFont}>{`OTA Reservation Number: ${booking?.channelBookingId}`}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      <Row className="mt-3 d-flex justify-content-center">
        <button
          type="submit"
          className={styles.settings_save}
          disabled={disableSubmit}
        >
          Save Changes
        </button>
      </Row>
    </Form>
  );
};

export default BookingDetails;
